import type {
  DeploymentRequestResourceItemBffV2Api,
  DeploymentRequestResourceListResponseBffV2Api,
  EnumDeploymentRequestStatusV2Api,
} from '@ingenico/api-sdk-device-management-private-v2';
import { enumDeploymentRequestStatusV2 } from '@ingenico/api-sdk-device-management-private-v2';
import { toString } from 'lodash-es';
import { isDateOnDateString } from '../../../../components/deployment-list/util/isDate';
import { isStringOnDateString } from '../../../../components/deployment-list/util/isStringOnDateString';
import { dashString } from '../../../../utils/dashString.util';
import type {
  DeploymentRow,
  DeploymentStatus,
  PaginatedDeploymentRows,
} from '../../../models/deployment/deploymentList.model';
import { computeConfigurationProfile, computeType } from '../../utils/deployment.util';

export function computeStatusLabel(input: EnumDeploymentRequestStatusV2Api): DeploymentStatus['label'] {
  switch (input) {
    case enumDeploymentRequestStatusV2.CANCELED: {
      return `status.canceled`;
    }
    case enumDeploymentRequestStatusV2.FAILED: {
      return `status.failed`;
    }
    case enumDeploymentRequestStatusV2.PROCESSING: {
      return `status.processing`;
    }
    case enumDeploymentRequestStatusV2.RUNNING: {
      return `status.running`;
    }
    case enumDeploymentRequestStatusV2.SCHEDULED: {
      return `status.scheduled`;
    }
    case enumDeploymentRequestStatusV2.PAUSED: {
      return `status.paused`;
    }
    case enumDeploymentRequestStatusV2.COMPLETED: {
      return `status.completed`;
    }
    default: {
      return ``;
    }
  }
}

export function computeStatusColor(input: EnumDeploymentRequestStatusV2Api): DeploymentStatus['color'] {
  switch (input) {
    case enumDeploymentRequestStatusV2.FAILED: {
      return 'failed';
    }
    case enumDeploymentRequestStatusV2.RUNNING:
    case enumDeploymentRequestStatusV2.PROCESSING: {
      return 'info';
    }
    case enumDeploymentRequestStatusV2.SCHEDULED:
    case enumDeploymentRequestStatusV2.PAUSED: {
      return 'warning';
    }
    case enumDeploymentRequestStatusV2.COMPLETED: {
      return 'success';
    }
    case enumDeploymentRequestStatusV2.CANCELED: {
      return 'neutral';
    }

    default: {
      return '';
    }
  }
}

export function getPriorityOrderField({
  trigger,
  startDate,
}: Pick<DeploymentRequestResourceItemBffV2Api, 'startDate' | 'trigger'>): Date | string {
  return (
    startDate ?? trigger.scheduleOption?.date ?? trigger.scheduleOption?.timeSlot?.startDate ?? 'startDate.unavailable'
  );
}

export function computeStartDate({
  trigger,
  startDate,
}: Pick<DeploymentRequestResourceItemBffV2Api, 'startDate' | 'trigger'>): string {
  const priorityField = getPriorityOrderField({
    trigger,
    startDate,
  });
  if (isStringOnDateString(priorityField)) {
    return priorityField;
  } else if (isDateOnDateString(priorityField)) {
    return priorityField.toISOString();
  }
  return '';
}

export function fromEnumDeploymentRequestStatusV2ApiToDeploymentStatus(
  input: EnumDeploymentRequestStatusV2Api,
): DeploymentStatus {
  return {
    label: computeStatusLabel(input),
    color: computeStatusColor(input),
  };
}

export function fromDeploymentRequestResourceItemBffV2ApiToDeploymentRow({
  name,
  description,
  creator: { name: creatorName },
  startDate,
  id,
  status,
  options,
  dynamicGroup: { deviceCount },
  trigger,
  creationDate,
}: DeploymentRequestResourceItemBffV2Api): DeploymentRow {
  return {
    id,
    name,
    description,
    type: computeType(options),
    configurationProfile: computeConfigurationProfile(options),
    startDate: computeStartDate({ trigger, startDate }),
    initiator: creatorName,
    targetDevices: dashString(toString(deviceCount)),
    status: fromEnumDeploymentRequestStatusV2ApiToDeploymentStatus(status),
    creationDate: isDateOnDateString(creationDate) ? creationDate?.toISOString() : creationDate,
  };
}

export function fromDeploymentRequestResourceListResponseV2ApiToPaginatedDeploymentRows({
  data,
  ...paginationData
}: DeploymentRequestResourceListResponseBffV2Api): PaginatedDeploymentRows {
  return { rows: data.map<DeploymentRow>(fromDeploymentRequestResourceItemBffV2ApiToDeploymentRow), ...paginationData };
}
